import {
  ACTIVATION_RULES_READ,
  ROLES_READ,
  USERS_READ,
  type PermissionRequirement,
} from '@/lib/permissions';

// Registro único de módulos del admin. Lo leen el sidebar (qué se ve), los
// guards de ruta (qué se abre) y el editor de roles (qué verá cada rol).
// Un módulo se ve y se abre solo si el user cumple TODOS sus `requires`.
//
// Para sumar un módulo: agregarlo aquí, enlazarlo en el menú
// (`src/components/layout/nav-groups.ts`) y montar `RequireModule` en
// `router.tsx`. Dashboard, `/integrators/:slug`, perfil y catálogo público no
// tienen requisito de módulo.

export type AppModuleKey =
  | 'products'
  | 'pending'
  | 'brands'
  | 'categories'
  | 'vehicleModels'
  | 'productImports'
  | 'aiSuggestions'
  | 'adminClients'
  | 'webhookDeliveries'
  | 'webhookBackfills'
  | 'users'
  | 'roles'
  | 'integrators'
  | 'auditLogs'
  | 'ingestEvents'
  | 'aiConfigs'
  | 'aiModels'
  | 'activationRules'
  | 'ragReindex'
  | 'queryLab'
  | 'photoStudio';

export interface AppModule {
  label: string;
  path: string;
  requires: readonly PermissionRequirement[];
}

export const APP_MODULES: Readonly<Record<AppModuleKey, AppModule>> = {
  products: {
    label: 'Productos',
    path: '/products',
    requires: ['products:read', 'products:write'],
  },
  pending: { label: 'Pendientes', path: '/pending', requires: ['products:read', 'products:write'] },
  brands: { label: 'Marcas', path: '/brands', requires: ['products:read', 'products:write'] },
  categories: {
    label: 'Categorías',
    path: '/categories',
    requires: ['products:read', 'products:write'],
  },
  vehicleModels: {
    label: 'Modelos vehículos',
    path: '/vehicle-models',
    requires: ['products:read', 'products:write'],
  },
  productImports: {
    label: 'Importaciones',
    path: '/admin/product-imports',
    requires: ['products:read', 'products:write'],
  },
  aiSuggestions: {
    label: 'Sugerencias IA',
    path: '/ai-suggestions',
    requires: ['ai:curate', 'products:read'],
  },
  // Release back 2026-09-21: `PATCH /admin/clients/:id` acepta `clients:write`
  // O `areas:configure` (acceso acotado a fieldPriorityConfig, allowedFields,
  // readOnlyFields y sourceSlugs). El requisito de abajo es la reescritura
  // por ley distributiva de "(clients:read Y clients:write) O areas:configure"
  // — equivalente booleano, pero expresable con el idioma AND-de-OR que ya
  // entienden `meetsRequirements`, el sidebar y `RequireModule`: un rol
  // completo (clients:read + clients:write) sigue entrando igual que antes,
  // y un rol con SOLO areas:configure también entra. La pantalla decide
  // adentro qué tabs/controles mostrarle a cada uno (`ClientDetailPage`).
  adminClients: {
    label: 'Clientes B2B',
    path: '/admin-clients',
    // `clients:read` SIEMPRE: `GET /admin/clients` lo exige y `areas:configure`
    // solo no alcanza (verificado con el backend el 2026-09-21). Sin esto, un
    // rol con únicamente `areas:configure` abría la pantalla y el listado le
    // devolvía 403 — justo el "entro y me rebota" que el modelo de permisos
    // existe para evitar.
    //
    // La escritura es any-of: el PATCH acepta `clients:write` O
    // `areas:configure`, y este último puede tocar nada más que los cuatro
    // campos de área (la pantalla esconde el resto).
    requires: ['clients:read', ['clients:write', 'areas:configure']],
  },
  webhookDeliveries: {
    label: 'Notificaciones enviadas',
    path: '/webhook-deliveries',
    requires: ['webhooks:operate', 'sources:inspect'],
  },
  webhookBackfills: {
    label: 'Reenviar notificaciones',
    path: '/admin/webhook-backfills',
    requires: ['webhooks:operate', 'clients:read'],
  },
  users: { label: 'Usuarios', path: '/users', requires: [USERS_READ, 'users:manage', ROLES_READ] },
  roles: { label: 'Roles', path: '/roles', requires: [ROLES_READ, 'roles:manage'] },
  integrators: {
    label: 'Integradores',
    path: '/admin/integrators',
    requires: ['integrations:manage', 'clients:read', ROLES_READ],
  },
  auditLogs: {
    label: 'Registro de cambios',
    path: '/audit-logs',
    requires: ['audit:read', USERS_READ],
  },
  ingestEvents: {
    label: 'Eventos de ingesta',
    path: '/admin/ingest-events',
    requires: ['ingest:replay', 'sources:inspect'],
  },
  aiConfigs: {
    label: 'Configuración IA',
    path: '/ai-configs',
    requires: ['ai:configure', 'products:read', 'sources:inspect'],
  },
  aiModels: { label: 'Modelos de IA', path: '/ai-models', requires: ['ai-models:manage'] },
  // Lectura (o `manage`, que la cumple) + escritura propias, y `products:read`
  // por lo que consume: los nombres de los campos salen del schema del
  // producto y el conteo de apagados enlaza al listado de productos.
  activationRules: {
    label: 'Reglas de activación',
    path: '/admin/activation-rules',
    requires: [ACTIVATION_RULES_READ, 'activation-rules:manage', 'products:read'],
  },
  ragReindex: { label: 'Reindexar RAG', path: '/admin/rag/reindex', requires: ['ai:configure'] },
  queryLab: {
    label: 'Query Lab',
    path: '/products/query',
    requires: ['sources:inspect', 'products:read'],
  },
  // Sección TEMPORAL (ver `src/features/photo-studio/index.ts`). Los endpoints
  // del estudio aceptan `media:upload` O `media:write`: el grupo interno es
  // any-of, igual que la escritura de `adminClients`.
  photoStudio: {
    label: 'Estudio de fotos',
    path: '/photo-studio',
    requires: ['products:read', ['media:upload', 'media:write']],
  },
};

// Lista ordenada (orden de declaración) para pantallas que recorren todos.
export const APP_MODULE_LIST: readonly (AppModule & { key: AppModuleKey })[] = (
  Object.keys(APP_MODULES) as AppModuleKey[]
).map((key) => ({ key, ...APP_MODULES[key] }));
